import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, fromEvent } from 'rxjs'
import { filter, take } from 'rxjs/operators'
import { importLibrary } from '@googlemaps/js-api-loader'

interface GoogleMapsWindow extends Window {
  google?: { maps?: unknown }
  initGoogleMaps?: () => void
  googleMapsLoaded?: boolean
}

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsService {
  private isLoaded$ = new BehaviorSubject<boolean>(false)
  private isLoading = false

  private get win(): GoogleMapsWindow {
    return window as GoogleMapsWindow
  }

  constructor() {
    // Check if Google Maps is already loaded
    if (typeof this.win.google !== 'undefined' && this.win.google.maps) {
      this.isLoaded$.next(true)
    } else {
      // Load Google Maps using the new importLibrary API
      this.loadGoogleMaps()

      // Listen for the custom event dispatched when Google Maps loads
      fromEvent(window, 'google-maps-loaded')
        .pipe(take(1))
        .subscribe(() => {
          this.isLoaded$.next(true)
        })

      // Fallback: Check window.googleMapsLoaded flag
      this.checkGoogleMapsLoaded()
    }
  }

  private async loadGoogleMaps(): Promise<void> {
    if (this.isLoading) return

    this.isLoading = true
    try {
      await importLibrary('maps')
      // Call the global callback function to maintain compatibility
      if (this.win.initGoogleMaps) {
        this.win.initGoogleMaps()
      }
    } catch (error) {
      console.error('Error loading Google Maps:', error)
    } finally {
      this.isLoading = false
    }
  }

  private checkGoogleMapsLoaded(): void {
    const checkInterval = setInterval(() => {
      if (this.win.googleMapsLoaded || (typeof this.win.google !== 'undefined' && this.win.google.maps)) {
        this.isLoaded$.next(true)
        clearInterval(checkInterval)
      }
    }, 100)

    // Clear the interval after 10 seconds to prevent infinite checking
    setTimeout(() => {
      clearInterval(checkInterval)
      if (!this.isLoaded$.value) {
        console.warn('Google Maps API failed to load within 10 seconds')
      }
    }, 10000)
  }

  /**
   * Returns an Observable that emits true when Google Maps API is loaded
   */
  isGoogleMapsLoaded(): Observable<boolean> {
    return this.isLoaded$.asObservable()
  }

  /**
   * Returns a Promise that resolves when Google Maps API is loaded
   */
  waitForGoogleMapsToLoad(): Promise<boolean> {
    if (this.isLoaded$.value) {
      return Promise.resolve(true)
    }

    return this.isLoaded$
      .pipe(
        filter((isLoaded) => isLoaded),
        take(1)
      )
      .toPromise()
      .then((isLoaded) => isLoaded ?? false)
  }

  /**
   * Returns true if Google Maps API is currently loaded
   */
  isCurrentlyLoaded(): boolean {
    return this.isLoaded$.value
  }
}
